import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import { createSelfAccount } from '../../services/counsellorService';
import Button from '../../components/common/Button/Button';
import cosmicLogo from '../../assets/images/cosmic-logo.png';
import styles from './CounsellorOnboarding.module.css';

const CounsellorOnboarding = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: user?.email || '',
    phone: '',
    whatsapp_phone: '',
    linkedin_profile: '',
    description: '',
    specialization: '',
    years_of_experience: 0,
    timezone: '',
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.details_added) {
      navigate('/counsellor/projects');
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = {
        ...formData,
        years_of_experience: parseInt(formData.years_of_experience, 10) || 0,
      };

      if (!payload.whatsapp_phone) {
        delete payload.whatsapp_phone;
      }

      await createSelfAccount(payload);
      await refreshUser();
      navigate('/counsellor/projects');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to complete onboarding. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className={styles.onboardingContainer}>
      <div className={styles.onboardingCard}>
        <div className={styles.header}>
          <img src={cosmicLogo} alt="COSMIC Logo" className={styles.logoImage} />
          <h1 className={styles.logo}>COSMIC</h1>
          <p className={styles.subtitle}>Complete Your Counsellor Profile</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="first_name" className={styles.label}>First Name *</label>
              <input
                id="first_name"
                name="first_name"
                type="text"
                value={formData.first_name}
                onChange={handleChange}
                className={styles.input}
                placeholder="First Name"
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="last_name" className={styles.label}>Last Name *</label>
              <input
                id="last_name"
                name="last_name"
                type="text"
                value={formData.last_name}
                onChange={handleChange}
                className={styles.input}
                placeholder="Last Name"
                required
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.label}>Email *</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              className={styles.input}
              disabled
              required
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="phone" className={styles.label}>Phone Number *</label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className={styles.input}
                placeholder="+1234567890"
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="whatsapp_phone" className={styles.label}>WhatsApp Number</label>
              <input
                id="whatsapp_phone"
                name="whatsapp_phone"
                type="tel"
                value={formData.whatsapp_phone}
                onChange={handleChange}
                className={styles.input}
                placeholder="+1234567890"
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="linkedin_profile" className={styles.label}>LinkedIn Profile URL</label>
            <input
              id="linkedin_profile"
              name="linkedin_profile"
              type="url"
              value={formData.linkedin_profile}
              onChange={handleChange}
              className={styles.input}
              placeholder="https://linkedin.com/in/username"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="description" className={styles.label}>Professional Summary</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={styles.textarea}
              placeholder="Tell us about your professional background and why you want to be a counsellor."
              rows="4"
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="specialization" className={styles.label}>Specialization</label>
              <input
                id="specialization"
                name="specialization"
                type="text"
                value={formData.specialization}
                onChange={handleChange}
                className={styles.input}
                placeholder="e.g., College Prep, STEM"
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="years_of_experience" className={styles.label}>Years of Experience</label>
              <input
                id="years_of_experience"
                name="years_of_experience"
                type="number"
                min="0"
                value={formData.years_of_experience}
                onChange={handleChange}
                className={styles.input}
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="timezone" className={styles.label}>Timezone</label>
            <input
              id="timezone"
              name="timezone"
              type="text"
              value={formData.timezone}
              onChange={handleChange}
              className={styles.input}
              placeholder="e.g., UTC-5, EST, etc."
            />
          </div>

          {error && (
            <div className={styles.error}>
              {error}
            </div>
          )}

          <Button type="submit" loading={loading}
            variant="primary"
            fullWidth
            disabled={loading}
          >
            {loading ? 'Saving Profile...' : 'Complete Onboarding'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CounsellorOnboarding;
